import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  Search, 
  Bell, 
  Clock, 
  CheckCircle, 
  ArrowRight, 
  Brain,
  Target,
  TrendingUp,
  Shield,
  Zap,
  Users,
  Calendar,
  Mail,
  Building2
} from 'lucide-react';

export default function VeilleMarchesPublicsPage() {
  return (
    <>
      <SEO 
        title="Veille Marchés Publics Automatisée | Alertes en Temps Réel"
        description="Automatisez votre veille des marchés publics avec notre plateforme IA. Alertes personnalisées, BOAMP, DECP, TED Europa analysés en continu. Essai gratuit."
        path="/veille-marches-publics"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <Breadcrumb items={[{ label: "Veille marchés publics", href: "/veille-marches-publics" }]} />
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm mb-6">
                <Bell className="w-4 h-4" />
                <span>Alertes en temps réel</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Veille Marchés Publics
                <br />
                <span className="text-violet-300">Automatisée par l'IA</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                Ne manquez plus jamais un appel d'offres pertinent. Notre intelligence artificielle surveille en continu les marchés publics et vous alerte instantanément.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                    Démarrer ma veille gratuite
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Qu'est-ce que la veille des marchés publics ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  La <strong>veille des marchés publics</strong> est une activité essentielle pour toute entreprise souhaitant répondre aux appels d'offres de l'État, des collectivités territoriales et des établissements publics. Elle consiste à surveiller en permanence les publications officielles pour identifier les opportunités commerciales correspondant à votre activité.
                </p>
                <p>
                  En France, les marchés publics représentent chaque année plus de <strong>200 milliards d'euros</strong> de commandes. C'est un vivier d'opportunités considérable pour les entreprises de toutes tailles, des TPE aux grands groupes.
                </p>
                <p>
                  Traditionnellement, cette veille nécessitait de consulter manuellement plusieurs sources : le BOAMP (Bulletin Officiel des Annonces des Marchés Publics), la plateforme PLACE, les sites des collectivités, le Journal Officiel de l'Union Européenne (TED)... Une tâche chronophage et souvent inefficace.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Sources surveillées */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Les sources que nous surveillons pour vous
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  title: "BOAMP",
                  desc: "Bulletin Officiel des Annonces des Marchés Publics - La source officielle française pour les marchés de l'État et des collectivités.",
                  icon: Building2
                },
                {
                  title: "DECP",
                  desc: "Données Essentielles de la Commande Publique - Base de données open data regroupant les marchés de plus de 40 000€.",
                  icon: Search
                },
                {
                  title: "TED Europa",
                  desc: "Tenders Electronic Daily - Le journal officiel européen pour les marchés dépassant les seuils communautaires.",
                  icon: Target
                },
                {
                  title: "Marchés Sécurisés",
                  desc: "Plateforme de dématérialisation utilisée par de nombreuses collectivités françaises.",
                  icon: Shield
                },
                {
                  title: "SNCF & ADP",
                  desc: "Marchés des grands donneurs d'ordres publics : transport ferroviaire et aéroports.",
                  icon: TrendingUp
                },
                {
                  title: "Autres sources",
                  desc: "Achat Public, E-marchés, portails régionaux et sectoriels spécialisés.",
                  icon: Zap
                }
              ].map((source, i) => (
                <Card key={`item-${i}`} className="border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                      <source.icon className="w-6 h-6 text-violet-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{source.title}</h3>
                    <p className="text-slate-600">{source.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Avantages de l'automatisation */}
        <section className="py-16 bg-violet-900 text-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Pourquoi automatiser votre veille des marchés publics ?
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[
                {
                  title: "Gain de temps considérable",
                  desc: "Plus besoin de consulter manuellement des dizaines de sites chaque jour. Notre IA fait le travail pour vous 24h/24."
                },
                {
                  title: "Réactivité maximale",
                  desc: "Soyez alerté dès la publication d'un marché correspondant à vos critères. Les premiers à répondre ont souvent un avantage."
                },
                {
                  title: "Couverture exhaustive",
                  desc: "Ne passez plus à côté d'opportunités. Nous surveillons plus de 6 sources officielles simultanément."
                },
                {
                  title: "Personnalisation avancée",
                  desc: "Définissez vos critères : secteur d'activité, zone géographique, montant, mots-clés... Recevez uniquement les marchés pertinents."
                }
              ].map((item, i) => (
                <div key={`item-${i}`} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-violet-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-slate-300">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comment ça marche */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Comment fonctionne notre veille automatisée ?
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {[
                  {
                    step: "1",
                    title: "Créez votre profil entreprise",
                    desc: "Renseignez votre secteur d'activité, vos codes CPV, votre zone géographique d'intervention et vos mots-clés métier. Notre système apprend vos préférences."
                  },
                  {
                    step: "2",
                    title: "Notre IA analyse les marchés en continu",
                    desc: "Chaque jour, des milliers de nouveaux appels d'offres sont publiés. Notre intelligence artificielle les analyse, les catégorise et les compare à votre profil."
                  },
                  {
                    step: "3",
                    title: "Recevez des alertes personnalisées",
                    desc: "Par email ou directement dans votre tableau de bord, vous êtes alerté dès qu'un marché correspond à vos critères. Avec un score de pertinence pour prioriser."
                  },
                  {
                    step: "4",
                    title: "Accédez aux DCE en un clic",
                    desc: "Téléchargez directement les dossiers de consultation, analysez-les avec notre outil de synthèse IA, et préparez votre réponse."
                  }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Types d'entreprises */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
              La veille marchés publics pour tous les types d'entreprises
            </h2>
            <div className="max-w-4xl mx-auto prose prose-lg text-slate-600">
              <p>
                Que vous soyez une <strong>TPE</strong>, une <strong>PME</strong> ou un <strong>grand groupe</strong>, la veille des marchés publics est un levier de croissance incontournable. Les marchés publics sont accessibles à toutes les entreprises, et de nombreux dispositifs favorisent même l'accès des petites structures :
              </p>
              <ul>
                <li><strong>Allotissement obligatoire</strong> : les acheteurs publics doivent diviser leurs marchés en lots pour permettre aux PME de candidater</li>
                <li><strong>Marchés réservés</strong> : certains marchés sont réservés aux structures d'insertion ou aux entreprises adaptées</li>
                <li><strong>Simplification administrative</strong> : le DUME (Document Unique de Marché Européen) facilite les candidatures</li>
                <li><strong>Avances et acomptes</strong> : les PME peuvent bénéficier d'avances allant jusqu'à 60% du montant du marché</li>
              </ul>
              <p>
                Avec DCE Express, nous vous aidons à identifier les marchés correspondant à votre taille et à vos capacités. Notre IA évalue la <strong>complexité</strong> de chaque marché et vous recommande ceux où vous avez les meilleures chances de succès.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Questions fréquentes sur la veille marchés publics
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {[
                {
                  q: "Combien coûte une veille des marchés publics ?",
                  a: "DCE Express propose une offre gratuite permettant de tester la plateforme. Les offres payantes démarrent à partir de 29€/mois avec des fonctionnalités avancées comme les alertes illimitées et l'analyse IA des DCE."
                },
                {
                  q: "Quelle est la différence entre le BOAMP et le JOUE/TED ?",
                  a: "Le BOAMP publie les marchés français, tandis que TED (Tenders Electronic Daily) publie les marchés européens dépassant les seuils communautaires. Notre plateforme surveille les deux pour une couverture complète."
                },
                {
                  q: "Comment définir mes critères de veille ?",
                  a: "Vous pouvez paramétrer votre veille selon votre secteur d'activité (codes CPV), votre zone géographique, les montants de marchés visés, et des mots-clés spécifiques à votre métier."
                },
                {
                  q: "À quelle fréquence sont mises à jour les données ?",
                  a: "Notre système collecte les nouvelles publications plusieurs fois par jour. Vous êtes alerté en temps réel dès qu'un marché correspondant à vos critères est détecté."
                }
              ].map((faq, i) => (
                <Card key={`item-${i}`} className="border-slate-200">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{faq.q}</h3>
                    <p className="text-slate-600">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Lancez votre veille marchés publics dès maintenant
            </h2>
            <p className="text-xl text-violet-100 mb-8 max-w-2xl mx-auto">
              Rejoignez les entreprises qui ne manquent plus jamais une opportunité. Inscription gratuite, sans engagement.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                Créer mon compte gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="veille" />

        <Footer />
      </div>
    </>
  );
}
